import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  listNotifications,
  markNotificationRead,
  type NotificationRow,
} from "@/shared/api/comments";
import { cn } from "@/lib/utils";

const POLL_MS = 45_000;
const MAX_VISIBLE = 3;

type Peek = NotificationRow & { expanded?: boolean; menuOpen?: boolean };

/**
 * Mac Messages-style live notif peeks — plan/50.
 * X = close only (not read). Mark read = explicit action. Open-doc ≠ auto-read.
 */
export function DeskNotifPeeks(props: {
  companySlug: string;
  enabled?: boolean;
}) {
  const { companySlug, enabled = true } = props;
  const navigate = useNavigate();
  const baselineRef = useRef<Set<string> | null>(null);
  const closedRef = useRef<Set<string>>(new Set());
  const [peeks, setPeeks] = useState<Peek[]>([]);

  useEffect(() => {
    if (!enabled || !companySlug) return;
    let cancelled = false;
    baselineRef.current = null;
    closedRef.current = new Set();
    setPeeks([]);

    async function refresh() {
      try {
        const res = await listNotifications(companySlug, true);
        if (cancelled) return;
        const unread = res.notifications.filter((n) => !n.readAt);
        if (baselineRef.current == null) {
          baselineRef.current = new Set(unread.map((n) => n.id));
          return;
        }
        const baseline = baselineRef.current;
        const fresh = unread.filter(
          (n) => !baseline.has(n.id) && !closedRef.current.has(n.id),
        );
        for (const n of fresh) baseline.add(n.id);
        if (fresh.length === 0) return;
        setPeeks((prev) => {
          const byId = new Map(prev.map((p) => [p.id, p]));
          for (const n of fresh) {
            if (!byId.has(n.id) && !closedRef.current.has(n.id)) {
              byId.set(n.id, { ...n, expanded: false, menuOpen: false });
            }
          }
          return Array.from(byId.values())
            .filter((p) => !closedRef.current.has(p.id))
            .slice(0, MAX_VISIBLE);
        });
      } catch {
        /* ignore poll errors */
      }
    }

    void refresh();
    const id = window.setInterval(() => void refresh(), POLL_MS);
    function onFocus() {
      void refresh();
    }
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, [companySlug, enabled]);

  function closePeek(id: string) {
    closedRef.current.add(id);
    setPeeks((prev) => prev.filter((p) => p.id !== id));
  }

  function patchPeek(id: string, patch: Partial<Peek>) {
    setPeeks((prev) =>
      prev.map((p) => (p.id === id ? { ...p, ...patch } : p)),
    );
  }

  async function onMarkRead(n: Peek) {
    try {
      await markNotificationRead(companySlug, n.id);
    } catch {
      /* still close peek */
    }
    closePeek(n.id);
  }

  function onOpen(n: Peek) {
    if (n.linkPath) {
      navigate(n.linkPath);
    } else {
      navigate(`/c/${companySlug}/inbox`);
    }
    // open-doc ≠ auto-read — leave unread; peek stays unless user X / Mark read
  }

  if (peeks.length === 0) return null;

  return (
    <div
      className="pointer-events-none fixed bottom-[max(1rem,env(safe-area-inset-bottom))] right-[max(1rem,env(safe-area-inset-right))] z-[80] flex w-[min(22rem,calc(100vw-1.5rem))] flex-col-reverse gap-2"
      aria-live="polite"
      aria-label="Notifications"
    >
      {peeks.map((n) => (
        <article
          key={n.id}
          className="pointer-events-auto rounded-[var(--radius-xl)] border border-[var(--color-border)] bg-[var(--color-card)] p-3 shadow-[var(--shadow-float)] animate-[fadeIn_200ms_ease-out]"
        >
          <div className="flex items-start gap-2">
            <button
              type="button"
              className="min-w-0 flex-1 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]"
              onClick={() => onOpen(n)}
            >
              <div className="text-sm font-semibold text-[var(--color-foreground)]">
                {n.title}
              </div>
              {n.body ? (
                <p
                  className={cn(
                    "mt-1 m-0 text-xs leading-relaxed text-[var(--color-muted)]",
                    !n.expanded && "line-clamp-2",
                  )}
                >
                  {n.body}
                </p>
              ) : null}
            </button>
            <div className="relative flex shrink-0 items-center gap-0.5">
              <div className="relative">
                <button
                  type="button"
                  className="inline-flex size-7 items-center justify-center rounded-full text-[var(--color-muted)] hover:bg-[var(--color-border)]/40 hover:text-[var(--color-foreground)]"
                  title="Actions"
                  aria-label="Notification actions"
                  aria-expanded={n.menuOpen}
                  onClick={() =>
                    patchPeek(n.id, { menuOpen: !n.menuOpen })
                  }
                >
                  ⋯
                </button>
                {n.menuOpen ? (
                  <>
                    <button
                      type="button"
                      className="fixed inset-0 z-[81] cursor-default bg-transparent"
                      aria-label="Dismiss menu"
                      onClick={() => patchPeek(n.id, { menuOpen: false })}
                    />
                    <ul
                      className="absolute right-0 top-full z-[82] mt-1 min-w-[10rem] list-none rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-1 shadow-[var(--shadow-float)]"
                      role="menu"
                    >
                      <li className="m-0 p-0">
                        <button
                          type="button"
                          role="menuitem"
                          className="flex w-full rounded-lg px-2.5 py-2 text-left text-sm hover:bg-[var(--color-background)]"
                          onClick={() => {
                            patchPeek(n.id, { menuOpen: false });
                            void onMarkRead(n);
                          }}
                        >
                          Mark as Read
                        </button>
                      </li>
                      <li className="m-0 p-0">
                        <button
                          type="button"
                          role="menuitem"
                          className="flex w-full rounded-lg px-2.5 py-2 text-left text-sm hover:bg-[var(--color-background)]"
                          onClick={() => {
                            patchPeek(n.id, { menuOpen: false });
                            onOpen(n);
                          }}
                        >
                          Open
                        </button>
                      </li>
                      {n.type === "approval_pending" ? (
                        <li className="m-0 p-0">
                          <button
                            type="button"
                            role="menuitem"
                            className="flex w-full rounded-lg px-2.5 py-2 text-left text-sm hover:bg-[var(--color-background)]"
                            onClick={() => {
                              patchPeek(n.id, { menuOpen: false });
                              navigate(`/c/${companySlug}/approvals`);
                            }}
                          >
                            Approvals
                          </button>
                        </li>
                      ) : null}
                      {n.type === "mention" && n.body ? (
                        <li className="m-0 p-0">
                          <button
                            type="button"
                            role="menuitem"
                            className="flex w-full rounded-lg px-2.5 py-2 text-left text-sm hover:bg-[var(--color-background)]"
                            onClick={() => {
                              patchPeek(n.id, {
                                menuOpen: false,
                                expanded: true,
                              });
                              onOpen(n);
                            }}
                          >
                            Reply
                          </button>
                        </li>
                      ) : null}
                      {n.body ? (
                        <li className="m-0 p-0">
                          <button
                            type="button"
                            role="menuitem"
                            className="flex w-full rounded-lg px-2.5 py-2 text-left text-sm hover:bg-[var(--color-background)]"
                            onClick={() =>
                              patchPeek(n.id, {
                                menuOpen: false,
                                expanded: !n.expanded,
                              })
                            }
                          >
                            {n.expanded ? "Show less" : "Expand"}
                          </button>
                        </li>
                      ) : null}
                    </ul>
                  </>
                ) : null}
              </div>
              <button
                type="button"
                className="inline-flex size-7 items-center justify-center rounded-full text-[var(--color-muted)] hover:bg-[var(--color-border)]/40 hover:text-[var(--color-foreground)]"
                title="Close"
                aria-label="Close notification"
                onClick={() => closePeek(n.id)}
              >
                ×
              </button>
            </div>
          </div>
        </article>
      ))}
    </div>
  );
}
